/**
 * Handy-dandy geometry functions
 */

function transformRing(transformation, ring) {
  // intentionally using the for-loop instead of map
  //  for when rings get really big and performance matters.
  const nextRing = [];
  for (let i = 0, ii = ring.length; i < ii; i++) {
    nextRing.push(transformation.forward(ring[i]));
  }
  return nextRing;
}

export function transformGeometry(transformation, geometry) {
  switch (geometry.type) {
    case 'MultiPoint':
    case 'LineString':
      return {
        ...geometry,
        coordinates: transformRing(transformation, geometry.coordinates),
      };
    case 'MultiLineString':
    case 'Polygon':
      return {
        ...geometry,
        coordinates: geometry.coordinates.map(ring =>
          transformRing(transformation, ring)
        ),
      };
    case 'MultiPolygon':
      return {
        ...geometry,
        coordinates: geometry.coordinates.map(polygon =>
          polygon.map(ring => transformRing(transformation, ring))
        ),
      };
    default:
      // assume this is a point.
      return {
        ...geometry,
        coordinates: transformRing(transformation, [geometry.coordinates])[0],
      };
  }
}

export function getGeometryTypes(collection) {
  const geomTypes = {};
  for (let f = 0, ff = collection.features.length; f < ff; f++) {
    const feature = collection.features[f];
    geomTypes[feature.geometry.type] = true;
  }
  // make an array.
  return Object.keys(geomTypes);
}

// deep level flatten recursion with reduce and concat
// borrowed from:
//  https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/flat
//
export function flatDeep(arr, d = 1) {
  return d > 0
    ? arr.reduce(
        (acc, val) =>
          acc.concat(Array.isArray(val) ? flatDeep(val, d - 1) : val),
        []
      )
    : arr.slice();
}

export function ringTest(geom) {
  const coords = geom.coordinates;
  switch (geom.type) {
    case 'Point':
      // handle 2d and 3d coordinates.
      return coords.length === 2 || coords.length === 3;
    case 'MultiPoint':
      return (
        coords.length >= 1 && coords.filter(pt => pt.length >= 2).length > 0
      );
    case 'LineString':
      return (
        coords.length >= 1 && coords.filter(pt => pt.length >= 2).length > 1
      );
    case 'MultiLineString':
      // require at least one line string with a length of min 2.
      return (
        coords.length >= 1 && coords.filter(ring => ring.length >= 2).length > 0
      );
    case 'Polygon':
      // require at least one ring, and at least 3 points per ring
      return (
        coords.length >= 1 && coords.filter(ring => ring.length >= 3).length > 0
      );
    case 'MultiPolygon':
      return (
        coords.length >= 1 &&
        coords.filter(polygon => {
          return polygon.length > 0 && polygon[0].length >= 3;
        }).length > 0
      );
    default:
      // if it cannot be tested then it cannot be valid.
      return false;
  }
}

export function isGeometryValid(geom) {
  if (geom && geom.coordinates && geom.coordinates.length > 0) {
    if (ringTest(geom)) {
      // this will flatten all rings of any geometry type
      const allCoords = flatDeep(geom.coordinates, Infinity);
      // ensure that allCoords is entirely finite.
      // Note: No filter/map conventions here, using the for-loop
      //       style to ensure higher performance.
      for (let i = 0, ii = allCoords.length; i < ii; i++) {
        // short circuit if anything is found that is not finite.
        if (!isFinite(allCoords[i])) {
          return false;
        }
      }
      // geometry is valid
      return true;
    } else {
      // failed ring test.
      return false;
    }
  }
  // if coordinates was null or undefined or length === 0
  //  then the geom is not valid.
  return false;
}
